import { Campo } from '@/componentes/campo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Input } from '@/componentes/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/componentes/ui/select';

import { mascararCnpj, mascararWhatsapp, REGIMES, type Erros, type Formulario } from './formulario';

/* Quem é a empresa: o cartão de identificação da primeira etapa.
 *
 * Um capítulo da tela, não um "componente de formulário" genérico: ele conhece
 * este formulário, estes cinco campos e a ordem em que eles são lidos. */
export function Identificacao({
  form,
  erros,
  podeEditar,
  aoAlterar,
}: {
  form: Formulario | null;
  erros: Erros;
  podeEditar: boolean;
  aoAlterar: <C extends keyof Formulario>(campo: C, valor: string) => void;
}) {
  return (
          <Card>
            <CardHeader>
              <CardTitle>Identificação</CardTitle>
              <CardDescription>O nome identifica a empresa na capa do laudo.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 @campos:grid-cols-2 @campos:gap-x-8">
              {/* Nome ocupa a linha inteira porque é o único dado desta etapa
                  que não tem tamanho previsível; os outros quatro cabem numa
                  metade e por isso vão em pares. */}
              <Campo
                rotulo="Nome da empresa"
                obrigatorio
                erro={erros.nome_empresa}
                className="@campos:col-span-2"
              >
                {(atributos) => (
                  <Input
                    {...atributos}
                    value={form?.nome_empresa ?? ''}
                    onChange={(e) => aoAlterar('nome_empresa', e.currentTarget.value)}
                    disabled={!podeEditar}
                    autoComplete="organization"
                    placeholder="Solar Norte Energia"
                  />
                )}
              </Campo>

              {/* CNPJ e regime dividem a linha porque respondem à mesma
                  pergunta (como a empresa é registrada) e cabem no mesmo
                  tamanho de célula. O "Opcional." saiu: o asterisco vermelho já
                  separa obrigatório de opcional na tela inteira, e repetir a
                  mesma informação em texto miúdo desalinhava a linha, porque o
                  campo com ajuda fica mais alto que o vizinho sem ela. */}
              <Campo rotulo="CNPJ" erro={erros.cnpj}>
                {(atributos) => (
                  <Input
                    {...atributos}
                    value={form?.cnpj ?? ''}
                    onChange={(e) => aoAlterar('cnpj', mascararCnpj(e.currentTarget.value))}
                    disabled={!podeEditar}
                    inputMode="numeric"
                    maxLength={18}
                    placeholder="00.000.000/0000-00"
                    className="numero"
                  />
                )}
              </Campo>

              <Campo rotulo="Regime tributário" obrigatorio erro={erros.regime_tributario}>
                {(atributos) => (
                  <Select
                    /* O `key` faz o Select NASCER com o regime já gravado, em vez
                       de recebê-lo depois. Sem isso o valor voltava vazio a cada
                       visita, e a causa não é óbvia: dentro de um <form>, o Radix
                       mantém um <select> espelho para o envio nativo; quando o
                       valor controlado chega DEPOIS da montagem, esse espelho
                       dispara um `change` antes de conhecer as opções e devolve
                       `onValueChange('')`. O eco caía no `alterar` e apagava o
                       regime que tinha acabado de ser carregado. Trocar a chave
                       quando o formulário deixa de ser nulo remonta o campo uma
                       vez, com o valor certo desde o primeiro render. */
                    key={form === null ? 'carregando' : 'carregado'}
                    value={form?.regime_tributario ?? ''}
                    onValueChange={(valor) => aoAlterar('regime_tributario', valor)}
                    disabled={!podeEditar}
                  >
                    <SelectTrigger {...atributos} className="w-full">
                      <SelectValue placeholder="Selecione o regime" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMES.map((regime) => (
                        <SelectItem key={regime} value={regime}>
                          {regime}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Campo>

              {/* Contato é IDENTIFICAÇÃO, não outro assunto: é por onde o
                  cliente responde a proposta que leva o nome da empresa. Um
                  cartão só para dois campos cobrava título, subtítulo e uma
                  parede de cera para dizer o que a linha já diz.

                  Cada controle ocupa a CÉLULA inteira, e quem carrega a medida
                  do dado é a célula (meia linha para telefone e CNPJ, linha
                  inteira para nome), não um max-width no controle: com o
                  telefone travado em 208px dentro de uma metade de 430 a caixa
                  parecia cortada ao meio ao lado do vizinho do mesmo tamanho de
                  dado. */}
              <Campo rotulo="E-mail" obrigatorio erro={erros.email}>
                {(atributos) => (
                  <Input
                    {...atributos}
                    type="email"
                    value={form?.email ?? ''}
                    onChange={(e) => aoAlterar('email', e.currentTarget.value)}
                    disabled={!podeEditar}
                    autoComplete="email"
                    placeholder="contato@suaempresa.com.br"
                  />
                )}
              </Campo>

              <Campo rotulo="WhatsApp" obrigatorio erro={erros.whatsapp}>
                {(atributos) => (
                  <Input
                    {...atributos}
                    type="tel"
                    value={form?.whatsapp ?? ''}
                    onChange={(e) => aoAlterar('whatsapp', mascararWhatsapp(e.currentTarget.value))}
                    disabled={!podeEditar}
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="(00) 00000-0000"
                    className="numero"
                  />
                )}
              </Campo>
            </CardContent>
          </Card>
  );
}
